import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { LoadingIndicator } from '../../shared/loading-indicator/loading-indicator';
import { AuthService } from '../../services/auth';
import { ApiEndpoints } from '../../core/api-endpoints';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Session } from '../../services/session';
import { SchoolService } from '../../services/school';
import { FlashService } from '../../services/flash';

@Component({
  selector: 'app-set-password',
  imports: [FormsModule, LoadingIndicator],
  templateUrl: './set-password.html',
  styleUrl: './set-password.css',
})
export class SetPassword implements OnInit {
  constructor(
    private authService: AuthService,
    private router: Router,
    private session: Session,
    private schoolService: SchoolService,
  ) {
    const token = session.getItem('token');

    if (!token) {
      return;
    }

    this.schoolId = this.session.decodeToken(token)?.school_id;

    if (!this.schoolId) {
      void this.router.navigate(['/']);
      return;
    }
  }

  ngOnInit(): void {
    if (!this.schoolId) {
      return;
    }

    this.getSchoolById(this.schoolId);
  }

  protected temporaryPassword: string = '';
  protected newPassword: string = '';
  protected confirmPassword: string = '';
  protected isLoading = signal<boolean>(false);
  protected showTempPassword: boolean = false;
  protected showNewPassword: boolean = false;
  protected showConfirmPassword: boolean = false;
  protected schoolId?: string;
  protected schoolName = signal('');
  protected schoolImage = signal('');
  protected schoolColor = signal<string | null>('');
  protected readonly flashService = inject(FlashService);

  protected getSchoolById(id: string): void {
    this.schoolService.getSchoolById(`${ApiEndpoints.url}/school/${id}`).subscribe({
      next: (data) => {
        this.schoolName.set(data.payload.school_name);
        this.schoolImage.set(data.payload.school_img);
        this.schoolColor.set(data.payload.primary_color);
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Unable to load school data. Refresh the page');
      },
    });
  }

  protected setPassword(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    if (this.newPassword === this.confirmPassword && this.newPassword === this.temporaryPassword) {
      this.flashService.failure('New password cannot be same as temporary password');
      return;
    }

    if (this.newPassword !== this.confirmPassword) {
      this.flashService.failure('Passwords do not match.');
      return;
    }

    if (!/\d/.test(this.confirmPassword)) {
      this.flashService.failure('Password must contain at least 1 numeric digit');
      return;
    }

    if (!/[!@#$%^&*]/.test(this.confirmPassword)) {
      this.flashService.failure('Password must contain at least 1 special characters.');
      return;
    }

    this.isLoading.set(true);

    const setPasswordForm = {
      temporaryPassword: this.temporaryPassword,
      newPassword: this.newPassword,
    };

    this.authService
      .setPassword(`${ApiEndpoints.url}/auth/set-password`, setPasswordForm)
      .subscribe({
        next: (res) => {
          this.session.removeItem('token');
          this.session.setItem('token', res.token);

          this.isLoading.set(false);

          this.flashService.success('Updated password successfully');

          if (
            res.payload.role === 'SCHOOL_ADMIN' &&
            (this.schoolColor() === null || this.schoolColor() === '')
          ) {
            void this.router.navigate(['/school-onboarding']);
          } else {
            void this.router.navigate(['/dashboard']);
          }
        },
        error: (err: HttpErrorResponse) => {
          const code = err.error?.code;
          this.isLoading.set(false);

          if (code === 'PASSWORD_RESET_UNREQUIRED') {
            this.flashService.failure(err.error?.message ?? 'Account does not require a password reset.');
            return;
          }

          if (code === 'USER_NOT_FOUND') {
            this.flashService.failure(err.error?.message ?? 'Account does not exist');
            return;
          }

          if (code === 'INVALID_TEMPORARY_PASSWORD') {
            this.flashService.failure(err.error?.message ?? 'Temporary password is incorrect');
            return;
          }

          this.flashService.failure(err.error?.message ?? 'Server error. Refresh the page');
        },
      });
  }
}
